import React, { useEffect, useState } from "react";
import Axios from "axios";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { api_url } from "../../../config";
import useStore from "../../../context/useStore";
import Control from "../../common/Control";
import Loader from "../../common/Loader";
import SaveBtn from "../../common/SaveBtn";
import { Pill } from "../../common/Fiche";

const JOURS = [
  { id: 1, name: "Lundi" },
  { id: 2, name: "Mardi" },
  { id: 3, name: "Mercredi" },
  { id: 4, name: "Jeudi" },
  { id: 5, name: "Vendredi" },
];
// Type de report envoyé à l'API (valeur historique, inchangée).
const TYPE_REPORT = "1";

export const formatDateRdv = (iso) => {
  const label = DateTime.fromISO(iso).setLocale("fr").toFormat("cccc d MMMM yyyy 'à' HH:mm");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const libelle = (liste, id) => {
  const item = (liste || []).find((c) => c.id == id); // eslint-disable-line eqeqeq
  return item ? item.name : id;
};

// Panneau de report d'un rendez-vous, affiché sous la carte concernée (à la
// place de l'ancienne fenêtre modale Bootstrap). Mêmes appels que l'ancien
// composant : lieux via l'orientation, créneaux du lieu, enregistrement.
const PanneauReport = ({ rdv, recipientId, rdspId, onDone, onCancel }) => {
  const [state] = useStore();
  const constants = state.constants.items;
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState(rdv.site.id);
  const [slots, setSlots] = useState(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [reasonSelected, setReasonSelected] = useState(null);
  const [daysSelected, setDaysSelected] = useState([]);
  const [rdvSelected, setRdvSelected] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    Axios.get(api_url + "rdsp/" + rdspId + "/orientation")
      .then((res) => setSites(res.data.sites))
      .catch((err) => {
        if (err.response && err.response.data) setErrors(err.response.data);
      });
  }, [rdspId]);

  const chercherDates = () => {
    setSlots(null);
    setRdvSelected(null);
    setDaysSelected([]);
    setErrors({});
    setIsLoadingSlots(true);
    Axios.post(api_url + "slotcalendars/sites/" + site, {
      userRecipientId: recipientId,
      reportType: TYPE_REPORT,
      cancelReason: reasonSelected,
      slotCalendarId: rdv.slotCalendarId,
    })
      .then((res) => setSlots(res.data))
      .catch((err) => {
        setSlots([]);
        if (err.response && err.response.data) setErrors(err.response.data);
      })
      .then(() => setIsLoadingSlots(false));
  };

  const enregistrer = () => {
    const slot = slots.find((s) => s.id == rdvSelected); // eslint-disable-line eqeqeq
    setIsSaving(true);
    Axios.post(api_url + "slotcalendars/recipients/" + recipientId, {
      slotsId: slot.slotsId,
      start: slot.start,
      end: slot.end,
      slotCalendarId: rdv.slotCalendarId,
      reportType: TYPE_REPORT,
      cancelReason: reasonSelected,
    })
      .then(() => onDone())
      .catch((err) => {
        if (err.response && err.response.data) {
          setErrors(err.response.data);
          if (err.response.data.other) toast.error(err.response.data.other);
        }
      })
      .then(() => setIsSaving(false));
  };

  const toggleJour = (e) => {
    const { value } = e.target;
    setDaysSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  // Seuls les jours de semaine qui ont au moins un créneau sont proposés.
  const joursDisponibles = slots
    ? JOURS.filter((j) => slots.some((s) => DateTime.fromISO(s.start).weekday === j.id))
    : [];
  const creneaux = slots ? slots.filter((s) => daysSelected.includes(DateTime.fromISO(s.start).weekday)) : [];

  return (
    <div className="rdv-report" role="region" aria-label="Reporter le rendez-vous">
      <h3 className="rdv-report__title">Reporter ce rendez-vous</h3>
      <Control
        label="Lieu du rendez-vous"
        type="select"
        name="siteReport"
        value={site}
        datas={sites}
        change={(e) => setSite(e.target.value)}
        dataIndex="id"
        dataLabel="name"
        error={errors}
        margin="mb-2"
      />
      <button type="button" className="btn btn-primary rdv-report__search" onClick={chercherDates} disabled={!site || isLoadingSlots}>
        Chercher des dates disponibles
      </button>

      {isLoadingSlots && <Loader />}
      {slots && slots.length === 0 && (
        <p className="rdv-report__vide" role="alert">
          {errors.other ||
            "Il n'y a pour l'instant aucun rendez-vous disponible, veuillez réessayer plus tard ou contacter votre agent référent."}
        </p>
      )}
      {slots && slots.length > 0 && (
        <>
          <Control
            label="Quel est le motif de ce report ?"
            name="reasonSelected"
            type="btnList"
            datas={constants.RDV_REPORT_TYPES}
            dataIndex="id"
            dataLabel="name"
            value={reasonSelected}
            change={(e) => setReasonSelected(e.target.value)}
            error={errors}
          />
          <Control
            label="Quels jours de la semaine êtes-vous disponible ?"
            type="checkBtnList"
            btnInline={true}
            name="days"
            datas={joursDisponibles}
            itemClassName="btn-sm"
            value={daysSelected}
            change={toggleJour}
          />
          {daysSelected.length > 0 && (
            <Control
              label="Choisissez la nouvelle date"
              type="btnList"
              datas={creneaux}
              name="rdvSlot"
              change={(e) => setRdvSelected(e.target.value)}
              value={rdvSelected}
              dataIndex="id"
              dataLabel="label"
              error={errors}
            />
          )}
        </>
      )}

      <div className="rdv-report__actions">
        {rdvSelected && (
          <SaveBtn save={enregistrer} isSaving={isSaving} text="Reporter le RDV à cette date" margin="" />
        )}
        <button type="button" className="btn btn-ghost" onClick={onCancel}>
          Annuler
        </button>
      </div>
    </div>
  );
};

const Resultat = ({ rdv, constants }) => {
  const { result } = rdv;
  return (
    <div className="rdv-card__resultat">
      <dl>
        <dt>Issue du rendez-vous</dt>
        <dd>{libelle(constants.RDV_CLOSE_TYPES, result.status)}</dd>
        {result.status == 2 && ( // eslint-disable-line eqeqeq
          <>
            <dt>Réorientation vers</dt>
            <dd>{result.newOrientation}</dd>
          </>
        )}
        {result.status == 3 && result.newDate && ( // eslint-disable-line eqeqeq
          <>
            <dt>Nouveau rendez-vous prévu le</dt>
            <dd>{formatDateRdv(result.newDate)}</dd>
          </>
        )}
        {result.status == 4 && ( // eslint-disable-line eqeqeq
          <>
            <dt>À l'initiative de</dt>
            <dd>{result.cancelOrigin || "Inconnu"}</dd>
            {result.cancelReason && (
              <>
                <dt>Motif</dt>
                <dd>{libelle(constants.RDV_REPORT_TYPES, result.cancelReason)}</dd>
              </>
            )}
          </>
        )}
      </dl>
    </div>
  );
};

// Liste des rendez-vous de l'allocataire : date en toutes lettres, lieu,
// commentaire, issue quand le rendez-vous est clôturé, report en ligne pour un
// rendez-vous à venir.
const MesRendezVous = ({ rdvs, recipientId, rdspId, onReported }) => {
  const [state] = useStore();
  const constants = state.constants.items;
  const [reportEnCours, setReportEnCours] = useState(null);
  const [message, setMessage] = useState("");

  const estAVenir = (rdv) => DateTime.fromISO(rdv.date) >= DateTime.local() && !rdv.result;

  return (
    <>
      {message && (
        <p className="rdv-list__status alert alert-success" role="status">
          {message}
        </p>
      )}
      <ul className="rdv-list">
        {rdvs.map((rdv, k) => {
          const aVenir = estAVenir(rdv);
          return (
            <li key={rdv.id || "rdv" + k} className={"rdv-card" + (aVenir ? " rdv-card--a-venir" : "")}>
              <div className="rdv-card__head">
                <div>
                  <p className="rdv-card__date">
                    <i className="fa fa-calendar-alt mr-2" aria-hidden="true" />
                    {formatDateRdv(rdv.date)}
                  </p>
                  <p className="rdv-card__lieu">
                    {rdv.site.organization.name} — {rdv.site.name}
                  </p>
                </div>
                {rdv.result ? (
                  <Pill tone="muted" icon="fa-check">
                    Terminé
                  </Pill>
                ) : aVenir ? (
                  <Pill tone="neutral" icon="fa-clock">
                    À venir
                  </Pill>
                ) : null}
              </div>
              {rdv.comment && rdv.comment !== "" && (
                <p className="rdv-card__comment">
                  <span className="sr-only">Votre commentaire : </span>
                  <i className="far fa-comment mr-2" aria-hidden="true" />
                  {rdv.comment}
                </p>
              )}
              {rdv.result && <Resultat rdv={rdv} constants={constants} />}
              {aVenir && reportEnCours !== rdv.id && (
                <button
                  type="button"
                  className="btn btn-ghost rdv-card__report mt-3"
                  onClick={() => {
                    setMessage("");
                    setReportEnCours(rdv.id);
                  }}
                >
                  <i className="fa fa-calendar-alt mr-2" aria-hidden="true" />
                  Reporter ce rendez-vous
                </button>
              )}
              {reportEnCours === rdv.id && (
                <PanneauReport
                  rdv={rdv}
                  recipientId={recipientId}
                  rdspId={rdspId}
                  onCancel={() => setReportEnCours(null)}
                  onDone={() => {
                    setReportEnCours(null);
                    setMessage("Votre rendez-vous a bien été reporté. La nouvelle date apparaît ci-dessous.");
                    onReported();
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
};

export default MesRendezVous;
